import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import NotificationCenter from '../../components/ui/NotificationCenter';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import SystemStatusIndicator from '../../components/ui/SystemStatusIndicator';
import KPICard from './components/KPICard';
import RevenueChart from './components/RevenueChart';
import FilterPanel from './components/FilterPanel';
import DataTable from './components/DataTable';
import ReportBuilder from './components/ReportBuilder';
import GeographicHeatmap from './components/GeographicHeatmap';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const RevenueAnalyticsReportingDashboard = () => {
  const navigate = useNavigate();
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isReportBuilderOpen, setIsReportBuilderOpen] = useState(false);
  const [isSystemStatusOpen, setIsSystemStatusOpen] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState('30d');
  const [filters, setFilters] = useState({});
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Mock KPI data
  const kpiData = [
    {
      title: 'Total Revenue',
      value: '$847,250',
      change: '+12.5%',
      changeType: 'positive',
      icon: 'DollarSign',
      subtitle: 'Last 30 days',
      trend: { percentage: 85, period: '30 days' }
    },
    {
      title: 'eRPC (Revenue Per Call)',
      value: '$48.75',
      change: '+8.2%',
      changeType: 'positive',
      icon: 'TrendingUp',
      subtitle: 'Average per call',
      trend: { percentage: 72, period: '30 days' }
    },
    {
      title: 'Win Rate',
      value: '67.8%',
      change: '-2.1%',
      changeType: 'negative',
      icon: 'Target',
      subtitle: 'Auction success rate',
      trend: { percentage: 68, period: '30 days' }
    },
    {
      title: 'Conversion Rate',
      value: '14.2%',
      change: '+3.4%',
      changeType: 'positive',
      icon: 'Users',
      subtitle: 'Lead to sale conversion',
      trend: { percentage: 78, period: '30 days' }
    },
    {
      title: 'Active Publishers',
      value: '1,247',
      change: '+18',
      changeType: 'positive',
      icon: 'Phone',
      subtitle: 'Publishing calls',
      trend: { percentage: 92, period: '30 days' }
    },
    {
      title: 'Active Buyers',
      value: '892',
      change: '+24',
      changeType: 'positive',
      icon: 'Building',
      subtitle: 'Participating in auctions',
      trend: { percentage: 88, period: '30 days' }
    }
  ];

  // Mock chart data
  const revenueChartData = [
    { date: '08/01', revenue: 28500, calls: 580, erpc: 49.14 },
    { date: '08/02', revenue: 31200, calls: 642, erpc: 48.60 },
    { date: '08/03', revenue: 29800, calls: 615, erpc: 48.46 },
    { date: '08/04', revenue: 33100, calls: 678, erpc: 48.82 },
    { date: '08/05', revenue: 35600, calls: 728, erpc: 48.90 },
    { date: '08/06', revenue: 32400, calls: 665, erpc: 48.72 },
    { date: '08/07', revenue: 30900, calls: 634, erpc: 48.74 },
    { date: '08/08', revenue: 34200, calls: 701, erpc: 48.79 },
    { date: '08/09', revenue: 36800, calls: 754, erpc: 48.81 },
    { date: '08/10', revenue: 33900, calls: 695, erpc: 48.78 },
    { date: '08/11', revenue: 31500, calls: 647, erpc: 48.69 },
    { date: '08/12', revenue: 37200, calls: 763, erpc: 48.75 },
    { date: '08/13', revenue: 35100, calls: 720, erpc: 48.75 },
    { date: '08/14', revenue: 32800, calls: 673, erpc: 48.74 },
    { date: '08/15', revenue: 38500, calls: 789, erpc: 48.79 },
    { date: '08/16', revenue: 34600, calls: 710, erpc: 48.73 },
    { date: '08/17', revenue: 36200, calls: 743, erpc: 48.72 },
    { date: '08/18', revenue: 39100, calls: 802, erpc: 48.75 }
  ];

  // Mock table data
  const publisherPerformanceData = [
    {
      publisher: 'Premium Lead Gen',
      revenue: 125000,
      calls: 2580,
      erpc: 48.45,
      conversion_rate: 0.156,
      payout: 87500,
      profit_margin: 0.30,
      traffic_source: 'Social Media'
    },
    {
      publisher: 'Direct Response Pro',
      revenue: 98500,
      calls: 2100,
      erpc: 46.90,
      conversion_rate: 0.142,
      payout: 68950,
      profit_margin: 0.30,
      traffic_source: 'Television'
    },
    {
      publisher: 'Insurance Leads Plus',
      revenue: 87200,
      calls: 1890,
      erpc: 46.14,
      conversion_rate: 0.138,
      payout: 61040,
      profit_margin: 0.30,
      traffic_source: 'Search Engine'
    },
    {
      publisher: 'Call Center Solutions',
      revenue: 76800,
      calls: 1650,
      erpc: 46.55,
      conversion_rate: 0.145,
      payout: 53760,
      profit_margin: 0.30,
      traffic_source: 'Radio'
    },
    {
      publisher: 'Digital Marketing Hub',
      revenue: 65400,
      calls: 1420,
      erpc: 46.06,
      conversion_rate: 0.134,
      payout: 45780,
      profit_margin: 0.30,
      traffic_source: 'Video Advertising'
    }
  ];

  const buyerActivityData = [
    {
      buyer: 'State Farm Regional',
      total_bids: 1250,
      won_auctions: 847,
      win_rate: 0.678,
      avg_bid: 52.30,
      total_spent: 44287,
      conversion_rate: 0.162,
      segment: 'Enterprise'
    },
    {
      buyer: 'Allstate Direct',
      total_bids: 980,
      won_auctions: 642,
      win_rate: 0.655,
      avg_bid: 49.80,
      total_spent: 31971,
      conversion_rate: 0.148,
      segment: 'Enterprise'
    },
    {
      buyer: 'Progressive Auto',
      total_bids: 1120,
      won_auctions: 728,
      win_rate: 0.650,
      avg_bid: 48.90,
      total_spent: 35599,
      conversion_rate: 0.155,
      segment: 'Enterprise'
    },
    {
      buyer: 'Liberty Mutual',
      total_bids: 890,
      won_auctions: 567,
      win_rate: 0.637,
      avg_bid: 51.20,
      total_spent: 29030,
      conversion_rate: 0.141,
      segment: 'Enterprise'
    },
    {
      buyer: 'GEICO Partners',
      total_bids: 1050,
      won_auctions: 672,
      win_rate: 0.640,
      avg_bid: 47.60,
      total_spent: 31987,
      conversion_rate: 0.159,
      segment: 'Enterprise'
    }
  ];

  const publisherColumns = [
    { key: 'publisher', header: 'Publisher', sortable: true },
    { key: 'revenue', header: 'Revenue', format: 'currency', sortable: true },
    { key: 'calls', header: 'Calls', format: 'number', sortable: true },
    { key: 'erpc', header: 'eRPC', format: 'currency', sortable: true },
    { key: 'conversion_rate', header: 'Conv. Rate', format: 'percentage', sortable: true },
    { key: 'payout', header: 'Payout', format: 'currency', sortable: true },
    { key: 'profit_margin', header: 'Margin', format: 'percentage', sortable: true },
    { key: 'traffic_source', header: 'Traffic Source', sortable: true }
  ];

  const buyerColumns = [
    { key: 'buyer', header: 'Buyer', sortable: true },
    { key: 'total_bids', header: 'Total Bids', format: 'number', sortable: true },
    { key: 'won_auctions', header: 'Won', format: 'number', sortable: true },
    { key: 'win_rate', header: 'Win Rate', format: 'percentage', sortable: true },
    { key: 'avg_bid', header: 'Avg Bid', format: 'currency', sortable: true },
    { key: 'total_spent', header: 'Total Spent', format: 'currency', sortable: true },
    { key: 'conversion_rate', header: 'Conv. Rate', format: 'percentage', sortable: true },
    { key: 'segment', header: 'Segment', sortable: true }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdated(new Date());
    }, refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [refreshInterval]);

  const handleQuickAction = (actionId, selectedItems) => {
    switch (actionId) {
      case 'generate_report':
        setIsReportBuilderOpen(true);
        break;
      case 'export_chart':
        console.log('Exporting chart data...');
        break;
      case 'schedule_report': console.log('Opening report scheduler...');
        break;
      case 'share_dashboard': console.log('Sharing dashboard...');
        break;
      default:
        console.log(`Action ${actionId} triggered with items:`, selectedItems);
    }
  };

  const handleSaveReport = (reportConfig) => {
    console.log('Saving report configuration:', reportConfig);
    // Here you would typically save to backend
  };

  const handleKeyboardShortcuts = (event) => {
    if (event?.ctrlKey || event?.metaKey) {
      switch (event?.key?.toLowerCase()) {
        case 'r':
          event?.preventDefault();
          setLastUpdated(new Date());
          break;
        case 'f':
          event?.preventDefault();
          setIsFilterOpen(!isFilterOpen);
          break;
        case 'n':
          event?.preventDefault();
          setIsReportBuilderOpen(true);
          break;
        default:
          break;
      }
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardShortcuts);
    return () => document.removeEventListener('keydown', handleKeyboardShortcuts);
  }, [isFilterOpen]);

  return (
    <div className="min-h-screen bg-background">
      <Header 
        userRole="analyst"
        isNotificationOpen={isNotificationOpen}
        onNotificationToggle={() => setIsNotificationOpen(!isNotificationOpen)}
      />
      <NotificationCenter 
        isOpen={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
      />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Revenue Analytics & Reporting</h1>
              <p className="text-muted-foreground mt-2">
                Comprehensive financial insights and performance metrics for marketplace optimization
              </p>
              <div className="flex items-center space-x-4 mt-3">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Clock" size={16} />
                  <span>Last updated: {lastUpdated?.toLocaleTimeString()}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <div className="w-2 h-2 bg-success rounded-full pulse-live"></div>
                  <span>Real-time data</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                iconName="Filter"
                iconPosition="left"
              >
                Filters
              </Button>
              <Button
                onClick={() => setIsReportBuilderOpen(true)}
                iconName="FileText"
                iconPosition="left"
              >
                Create Report
              </Button>
            </div>
          </div>

          {/* Quick Action Toolbar */}
          <div className="mb-6">
            <QuickActionToolbar
              section="analytics"
              selectedItems={[]}
              onAction={handleQuickAction}
            />
          </div>

          {/* Filter Panel */}
          <div className="mb-6">
            <FilterPanel
              isOpen={isFilterOpen}
              onToggle={() => setIsFilterOpen(!isFilterOpen)}
              onFiltersChange={setFilters}
              currentFilters={filters}
            />
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {kpiData?.map((kpi, index) => (
              <KPICard key={index} {...kpi} />
            ))}
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <RevenueChart
              title="Revenue Trend"
              data={revenueChartData}
              type="line"
              height={350}
            />
            <RevenueChart
              title="Call Volume & eRPC"
              data={revenueChartData?.map(item => ({ ...item, calls: item?.calls }))}
              type="bar"
              height={350}
            />
          </div>

          {/* Geographic Performance */}
          <div className="mb-8">
            <GeographicHeatmap
              title="Geographic Performance Analysis"
              data={[]}
              metric="revenue"
            />
          </div>

          {/* Data Tables */}
          <div className="space-y-8">
            <DataTable
              title="Publisher Performance Analysis"
              data={publisherPerformanceData}
              columns={publisherColumns}
              pageSize={10}
              exportable={true}
              searchable={true}
            />

            <DataTable
              title="Buyer Activity & Bidding Analysis"
              data={buyerActivityData}
              columns={buyerColumns}
              pageSize={10}
              exportable={true}
              searchable={true}
            />
          </div>

          {/* Revenue Breakdown by Traffic Source */}
          <div className="mt-8">
            <div className="bg-card border border-border rounded-lg p-6 shadow-card">
              <h3 className="text-lg font-semibold text-foreground mb-6">Revenue by Traffic Source</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { source: 'Social Media', revenue: 245000, percentage: 28.9, calls: 5020 },
                  { source: 'Television', revenue: 198000, percentage: 23.4, calls: 4100 },
                  { source: 'Search Engine', revenue: 167000, percentage: 19.7, calls: 3450 },
                  { source: 'Radio', revenue: 142000, percentage: 16.8, calls: 2980 },
                  { source: 'Video Ads', revenue: 95250, percentage: 11.2, calls: 1950 }
                ]?.map((item, index) => (
                  <div key={index} className="p-4 bg-muted/20 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-foreground text-sm">{item?.source}</h4>
                      <span className="text-xs text-muted-foreground">{item?.percentage}%</span>
                    </div>
                    <div className="text-xl font-bold text-foreground mb-1">
                      ${item?.revenue?.toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {item?.calls?.toLocaleString()} calls
                    </div>
                    <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all duration-500"
                        style={{ width: `${item?.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Report Builder Modal */}
      <ReportBuilder
        isOpen={isReportBuilderOpen}
        onClose={() => setIsReportBuilderOpen(false)}
        onSave={handleSaveReport}
      />
      {/* System Status Indicator */}
      <SystemStatusIndicator
        isExpanded={isSystemStatusOpen}
        onToggle={() => setIsSystemStatusOpen(!isSystemStatusOpen)}
      />
    </div>
  );
};

export default RevenueAnalyticsReportingDashboard;